// Conjunctions — the joiners. Coordinating (y/e, o/u, pero, sino) and
// subordinating (que, porque, aunque, cuando, si…), plus the spelling swaps
// that catch everyone. A reference table groups the common ones at the bottom.
import { renderGuideSections } from "../ui/guideSections.js";

const COORD = [
  ["y / e", "and", "café y té · madre e hija"],
  ["o / u", "or", "uno o dos · siete u ocho"],
  ["pero", "but", "quiero pero no puedo"],
  ["sino", "but rather", "no es rojo sino azul"],
  ["ni", "nor", "ni tú ni yo"],
];
const SUBORD = [
  ["que", "that", "creo que sí"],
  ["porque", "because", "porque quiero"],
  ["aunque", "although / even if", "aunque llueve"],
  ["cuando", "when", "cuando llegues"],
  ["si", "if", "si puedes"],
  ["mientras", "while", "mientras estudio"],
  ["para que", "so that", "para que aprendas"],
  ["como", "as / since", "como es tarde"],
];

export function conjunctionsView(app) {
  const block = (title, rows) => `
    <div class="bg-white rounded-2xl border border-sage-100 p-5">
      <h3 class="text-lg font-bold text-sage-800 mb-3">${title}</h3>
      <div class="overflow-x-auto"><table class="w-full text-sm">
        <thead><tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
          <th class="py-2 pr-3">Spanish</th><th class="py-2 px-3">English</th><th class="py-2 px-3">Example</th>
        </tr></thead>
        <tbody>
          ${rows
            .map(
              ([es, en, ex]) => `<tr class="border-b border-sage-50 last:border-0">
            <td class="py-2 pr-3 font-medium text-sage-800">${es}</td>
            <td class="py-2 px-3 text-sage-700">${en}</td>
            <td class="py-2 px-3 text-sage-500 italic">${ex}</td>
          </tr>`
            )
            .join("")}
        </tbody>
      </table></div>
    </div>`;

  const table = `
    <div class="grid gap-4 mt-6">
      ${block("Coordinating", COORD)}
      ${block("Subordinating", SUBORD)}
    </div>`;

  renderGuideSections(
    app,
    "Conjunctions",
    "Conjunctions glue words, phrases, and clauses together. The coordinating ones join equals; the subordinating ones hang one idea off another — and a couple of them trigger the subjunctive.",
    [
      {
        title: "1. The spelling swaps: y→e, o→u",
        body: `
          <p class="text-sage-700 mb-2">To avoid two identical vowel sounds colliding, two conjunctions change spelling:</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><b>y → e</b> before a word starting with <i>i-</i> or <i>hi-</i>: <i>madre <b>e</b> hija</i>, <i>España <b>e</b> Italia</i> (but stays <i>y</i> before <i>hie-</i>: <i>agua y hielo</i>).</li>
            <li><b>o → u</b> before a word starting with <i>o-</i> or <i>ho-</i>: <i>siete <b>u</b> ocho</i>, <i>mujer <b>u</b> hombre</i>.</li>
          </ul>
        `,
      },
      {
        title: "2. pero vs sino",
        body: `
          <p class="text-sage-700 mb-2">Both mean <i>but</i>, but they aren't interchangeable.</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><b>pero</b> adds a contrasting idea: <i>Es caro <b>pero</b> bueno.</i></li>
            <li><b>sino</b> corrects a negative — "not X but (rather) Y": <i>No es caro <b>sino</b> barato.</i></li>
            <li>Use <b>sino que</b> before a conjugated verb: <i>No canta <b>sino que</b> grita.</i></li>
          </ul>
        `,
      },
      {
        title: "3. Conjunctions that trigger the subjunctive",
        body: `
          <p class="text-sage-700 mb-2">Some subordinators introduce something not-yet-real, so the following verb goes subjunctive:</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><b>para que</b> (so that): <i>Te lo digo <b>para que</b> lo <b>sepas</b>.</i></li>
            <li><b>cuando</b> + future event: <i>Cuando <b>llegues</b>, llámame.</i></li>
            <li><b>aunque</b> + hypothetical: <i>Aunque <b>llueva</b>, saldré.</i> (even if it rains)</li>
            <li><b>antes de que</b>: <i>antes de que <b>sea</b> tarde.</i></li>
          </ul>
        `,
      },
    ],
    "",
    table
  );
  window.scrollTo({ top: 0, behavior: "smooth" });
}
